import React, { useState, useEffect } from 'react';
import AutocompleteSelect from '@/components/common/AutocompleteSelect';
import { FaCheck, FaUser, FaCar, FaFileAlt, FaStickyNote, FaClock, FaStopwatch, FaDollarSign, FaPlus, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import Button from '@/components/common/Button';
import { normalizeDataObject } from '@/utils/normalizationUtils';
import ModalCreacionRapida from './ModalCreacionRapida';
import ValidatedInput from '@/components/common/ValidatedInput';

/**
 * Componente de formulario para crear/editar citas
 */
const AppointmentForm = ({
  formData,
  selectedCliente,
  clienteData,
  isAdmin,
  clientes,
  filteredVehiculos,
  servicios,
  selectedServicios,
  selectedTime,
  horaFin,
  horaFinAjustada,
  tiempoTotal,
  loading,
  editingCita,
  handleChange,
  handleServicioSelect,
  handleDeseleccionarTodos,
  handleCancel,
  handleSubmit,
  onClienteVehiculoCreado // Nueva prop para manejar cliente/vehículo creado
}) => {
  const [showModalCreacionRapida, setShowModalCreacionRapida] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  
  // Efecto simplificado - solo se ejecuta una vez cuando se monta el componente para edición
  React.useEffect(() => {
    // Solo ejecutar cuando se inicia la edición de una cita (una sola vez)
    if (editingCita && formData && formData.id) {
      // Los datos ya están correctamente establecidos en formData desde CalendarioCitas
      // No necesitamos forzar selecciones adicionales
    }
  }, [editingCita]); // Solo depender de editingCita

  // Efecto para auto-seleccionar vehículo cuando hay solo uno disponible
  React.useEffect(() => {
    // Solo ejecutar si no estamos editando y hay exactamente un vehículo filtrado
    if (!editingCita && filteredVehiculos && filteredVehiculos.length === 1 && !formData.Vehiculo) {
      // filteredVehiculos contiene objetos de vehículo; el id corresponde a la patente
      handleChange({ target: { name: 'Vehiculo', value: filteredVehiculos[0].id } });
    }
  }, [filteredVehiculos, editingCita, formData.Vehiculo, handleChange]);
  // Función para calcular el precio total de los servicios seleccionados
  const calcularPrecioTotal = () => {
    if (!selectedServicios || selectedServicios.length === 0) return 0;
    
    return selectedServicios.reduce((total, servicio) => {
      // Normalizar servicio seleccionado para consistencia
      const normalizedServicio = normalizeDataObject(servicio, 'servicio');
      
      // Obtener el precio del servicio seleccionado
      let precio = 0;
      
      // Si el servicio ya tiene un precio, usarlo directamente
      if (normalizedServicio.Precio) {
        precio = Number(normalizedServicio.Precio) || 0;
      } else {
        // Si no tiene precio, buscar el servicio en la lista completa
        const servicioCompleto = servicios.find(s => {
          const normalizedS = normalizeDataObject(s, 'servicio');
          return normalizedS.id === normalizedServicio.id || 
                 (normalizedServicio.Servicio && 
                  (normalizedS.id === normalizedServicio.Servicio || 
                   (Array.isArray(normalizedServicio.Servicio) && 
                    normalizedServicio.Servicio.includes(normalizedS.id))));
        });
        if (servicioCompleto) {
          const normalizedServiceCompleto = normalizeDataObject(servicioCompleto, 'servicio');
          if (normalizedServiceCompleto.Precio) {
            precio = Number(normalizedServiceCompleto.Precio) || 0;
          }
        }
      }
      
      return total + precio;
    }, 0);
  };
  
  // Calcular el precio total
  const precioTotal = calcularPrecioTotal();

  // Handle when a new client/vehicle is created
  const handleClienteVehiculoCreado = (cliente, vehiculo) => {
    //console.log('Cliente y vehículo creados:', { cliente, vehiculo });
    
    // Seleccionar automáticamente el cliente creado
    if (cliente && cliente.id) {
      handleChange({ target: { name: 'Cliente', value: cliente.id } });
    }
    
    // Seleccionar automáticamente el vehículo creado
    if (vehiculo && vehiculo.id) {
      handleChange({ target: { name: 'Vehiculo', value: vehiculo.id } });
    }
    
    // Cerrar el modal de creación rápida
    setShowModalCreacionRapida(false);
    
    // Llamar al callback original si existe
    if (onClienteVehiculoCreado) {
      onClienteVehiculoCreado(cliente, vehiculo);
    }
  };

  // Función para validar todos los campos requeridos
  const validateAllFields = () => {
    let hasErrors = false;
    
    // Mostrar errores de validación
    setShowValidationErrors(true);
    
    // Validar cliente (solo para admins)
    if (isAdmin && (!selectedCliente || selectedCliente === '')) {
      hasErrors = true;
    }
    
    // Validar vehículo
    if (!formData.Vehiculo || formData.Vehiculo === '') {
      hasErrors = true;
    }
    
    // Validar motivo
    if (!formData.Motivo || formData.Motivo.trim() === '') {
      hasErrors = true;
    }
    
    // Validar servicios seleccionados
    if (!selectedServicios || selectedServicios.length === 0) {
      hasErrors = true;
    }
    
    return !hasErrors;
  };
  
  // Wrapper para handleSubmit con validación
  const handleSubmitWithValidation = (e) => {
    e.preventDefault();
    
    // Validar campos requeridos
    if (!validateAllFields()) {
      return;
    }
    
    handleSubmit(e);
  };

  return (
    <>
      <form onSubmit={handleSubmitWithValidation} className="text-white">

      <div className="flex flex-col lg:flex-row lg:space-x-6">
        {/* Columna izquierda - Información de la cita */}
        <div className="lg:w-1/2 space-y-4">
            {/* Si es admin, mostrar selector de cliente */}
            {isAdmin && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="flex items-center text-sm font-medium">
                    <FaUser className="text-gray-400 mr-2 text-xs" />
                    Cliente<span className="text-red-500 ml-1">*</span>
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowModalCreacionRapida(true)}
                    className="flex items-center gap-2"
                  >
                    <FaPlus className="text-xs" />
                    Nuevo Cliente y Vehículo
                  </Button>
                </div>
                <AutocompleteSelect
                  value={selectedCliente || ''}
                  onChange={(value) => handleChange({ target: { name: 'Cliente', value } })}
                  placeholder="Buscar cliente..."
                  displayProperty="label"
                  valueProperty="value"
                  secondaryProperty="telefono"
                  options={clientes.map((cliente) => {
                    const normalizedCliente = normalizeDataObject(cliente, 'cliente');
                    return {
                      value: normalizedCliente.id || normalizedCliente.Id,
                      label: `${normalizedCliente.Nombre || normalizedCliente.nombre || ''} ${normalizedCliente.Apellido || normalizedCliente.apellido || ''}`.trim(),
                      telefono: normalizedCliente.Telefono || normalizedCliente.telefono || ''
                    };
                  })}
                  className={showValidationErrors && (!selectedCliente || selectedCliente === '') ? 'error-border' : ''}
                  key={`cliente-select-${selectedCliente || 'empty'}`}
                />
                {/* Error message para cliente */}
                {showValidationErrors && isAdmin && (!selectedCliente || selectedCliente === '') && (
                  <p className="validation-message">
                    <FaExclamationTriangle />
                    Debe seleccionar un cliente
                  </p>
                )}
              </div>
            )}
            
            {/* Para clientes no admin, NO mostrar campo cliente - usar input hidden */}
            {!isAdmin && clienteData && (
              <input type="hidden" name="Cliente" value={clienteData.id} />
            )}

            {/* Vehículo */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaCar className="text-gray-400 mr-2 text-xs" />
                Vehículo<span className="text-red-500 ml-1">*</span>
              </label>
              <AutocompleteSelect
                value={formData.Vehiculo || ''}
                onChange={(value) => handleChange({ target: { name: 'Vehiculo', value } })}
                placeholder="Buscar vehículo..."
                displayProperty="label"
                valueProperty="value"
                isVehicleSelect={true}
                disabled={isAdmin && !selectedCliente}
                options={filteredVehiculos && filteredVehiculos.length > 0 ? 
                  filteredVehiculos.map((vehiculo) => {
                    const normalizedVehiculo = normalizeDataObject(vehiculo, 'vehiculo');
                    const patente = normalizedVehiculo.Patente || normalizedVehiculo.patente || '';
                    const marca = normalizedVehiculo.Marca || normalizedVehiculo.marca_local || '';
                    const modelo = normalizedVehiculo.Modelo || normalizedVehiculo.modelo_local || '';
                    return {
                      value: normalizedVehiculo.id || normalizedVehiculo.Id,
                      label: `${marca} ${modelo}${patente ? ` - ${patente}` : ''}`,
                      Marca: marca,
                      Modelo: modelo,
                      Patente: patente,
                      Año: normalizedVehiculo.Año || normalizedVehiculo.año || '',
                      Color: normalizedVehiculo.Color || normalizedVehiculo.color || ''
                    };
                  }) : []
                }
                className={showValidationErrors && (!formData.Vehiculo || formData.Vehiculo === '') ? 'error-border' : ''}
                key={`vehiculo-select-${formData.Vehiculo || 'empty'}-${selectedCliente || 'no-client'}`}
              />
              {/* Error message para vehículo */}
              {showValidationErrors && (!formData.Vehiculo || formData.Vehiculo === '') && (
                <p className="validation-message">
                  <FaExclamationTriangle />
                  Debe seleccionar un vehículo
                </p>
              )}
              {!isAdmin && filteredVehiculos && filteredVehiculos.length === 0 && (
                <p className="text-xs text-yellow-400 mt-1">
                  No tienes vehículos registrados. Contacta al taller para registrar tu vehículo.
                </p>
              )}
              {isAdmin && !selectedCliente && (
                <p className="text-xs text-gray-400 mt-1">
                  Primero selecciona un cliente para ver sus vehículos
                </p>
              )}
              {isAdmin && filteredVehiculos && filteredVehiculos.length === 0 && selectedCliente && (
                <p className="text-xs text-yellow-400 mt-1">
                  No hay vehículos registrados para este cliente
                </p>
              )}
            </div>
            
            {/* Motivo */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaFileAlt className="text-gray-400 mr-2 text-xs" />
                Motivo<span className="text-red-500 ml-1">*</span>
              </label>
              <ValidatedInput
                type="text"
                name="Motivo"
                placeholder="Motivo de la cita"
                value={formData.Motivo}
                onChange={handleChange}
                required
                requiredMessage="El motivo es requerido"
                forceShowValidation={showValidationErrors}
              />
            </div>
            
            {/* Detalles */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaStickyNote className="text-gray-400 mr-2 text-xs" />
                Detalles (opcional)
              </label>
              <textarea
                name="Detalles"
                placeholder="Detalles adicionales"
                value={formData.Detalles}
                onChange={handleChange}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                rows={1}
              />
            </div>
            
            {/* Información de hora - Solo visible en desktop */}
            <div className="hidden lg:block bg-gray-700 rounded-lg p-3 mt-4 border border-gray-600">
              {/* Fila con inicio y fin */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <span className="text-gray-400 whitespace-nowrap">Inicio:</span>
                  <span className="ml-2 text-white font-bold">{selectedTime?.format('HH:mm')}</span>
                </div>
                {horaFin && (
                  <div className="flex items-center">
                    <span className="text-gray-400 whitespace-nowrap">Fin:</span>
                    <span className="ml-2 text-white font-bold">
                      {horaFin.format('HH:mm')}
                      {/* Mostrar la fecha solo cuando la hora de fin fue ajustada por el horario de cierre */}
                      {horaFinAjustada && selectedTime && 
                       horaFin.format('YYYY-MM-DD') !== selectedTime.format('YYYY-MM-DD') && (
                        <span className="ml-1 text-yellow-400 text-xs">
                          ({horaFin.format('DD/MM/YYYY')})
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Fila con duración centrada y ocupando todo el ancho */}
              {tiempoTotal > 0 && (
                <div className="flex justify-center w-full">
                  <div className="bg-blue-600 text-white px-4 py-1.5 rounded-md flex items-center justify-center w-full">
                    <span className="font-medium">Duración: {tiempoTotal} min</span>
                  </div>
                </div>
              )}
            </div>
        </div>
        
        {/* Columna derecha - Servicios */}
        <div className="lg:w-1/2 mt-4 lg:mt-0">
          {/* Servicios a realizar */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium">Servicios a realizar</label>
              {selectedServicios.length > 0 && (
                <button 
                  type="button" 
                  onClick={handleDeseleccionarTodos}
                  className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Deseleccionar todos
                </button>
              )}
              {selectedServicios.length === 0 && !showValidationErrors && (
                <p className="text-xs text-yellow-400 mt-1">
                  Selecciona al menos un servicio
                </p>
              )}
            </div>
            <div className="border border-gray-700 rounded-lg overflow-hidden">
              <div className="md:max-h-[400px] md:overflow-y-auto">
                {servicios.length === 0 ? (
                  <p className="p-3 text-gray-400 text-center">No hay servicios disponibles</p>
                ) : (
                  <div className="divide-y divide-gray-700">
                    {/* Error message para servicios */}
                    {showValidationErrors && (!selectedServicios || selectedServicios.length === 0) && (
                      <div className="p-3 bg-red-900/20 border-l-4 border-red-500">
                        <p className="validation-message">
                          <FaExclamationTriangle />
                          Debe seleccionar al menos un servicio
                        </p>
                      </div>
                    )}
                    {servicios
                      .filter(servicio => servicio.activo !== false && servicio.Activo !== false)
                      .map(servicio => {
                      // Normalizar servicio para consistencia
                      const normalizedServicio = normalizeDataObject(servicio, 'servicio');
                      
                      // Verificar si el servicio está seleccionado usando una función más eficiente
                      const isSelected = selectedServicios.some(s => 
                        // Comparar ID directo
                        s.id === normalizedServicio.id ||
                        // Comparar campo Servicio (string)
                        (s.Servicio && typeof s.Servicio === 'string' && s.Servicio === normalizedServicio.id) ||
                        // Comparar campo Servicio (array)
                        (s.Servicio && Array.isArray(s.Servicio) && s.Servicio.includes(normalizedServicio.id))
                      );
                      return (
                        <div 
                          key={normalizedServicio.id} 
                          className={`p-3 relative flex items-start cursor-pointer transition-colors ${isSelected ? 'bg-blue-900/50' : 'hover:bg-gray-700'}`}
                          onClick={() => handleServicioSelect(normalizedServicio.id)}
                        >
                          {/* Checkbox y detalles del servicio */}
                          <div className="flex-grow pr-16"> {/* Añadir padding-right para dejar espacio para el badge de precio */}
                            <div className="flex items-center">
                              <div className={`w-4 h-4 rounded-sm mr-3 flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-blue-500' : 'border border-gray-500'}`}>
                                {isSelected && (
                                  <FaCheck className="text-white text-xs" />
                                )}
                              </div>
                              <div>
                                <h4 className="font-medium leading-tight">{normalizedServicio.Nombre || normalizedServicio.nombre || ''}</h4>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Tiempo: {String(normalizedServicio.TiempoEstimado || normalizedServicio.Tiempo || 30)} min
                                  <span className="mx-1 text-gray-500">•</span>
                                  <span className="capitalize">
                                    {(() => {
                                      const categoria = normalizedServicio.Categoria || normalizedServicio.categoria;
                                      if (typeof categoria === 'object' && categoria !== null) {
                                        return categoria.nombre || categoria.Nombre || 'General';
                                      }
                                      return categoria || 'General';
                                    })()}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Badge de precio en la esquina inferior derecha */}
                          {typeof normalizedServicio.Precio !== 'undefined' && normalizedServicio.Precio > 0 ? (
                            <div className="absolute bottom-3 right-3 bg-green-800 text-green-300 text-xs font-medium px-2 py-0.5 rounded">
                              ${Number(normalizedServicio.Precio).toLocaleString()}
                            </div>
                          ) : (
                            <div className="absolute bottom-3 right-3 bg-yellow-800/50 text-yellow-300 text-xs font-medium px-2 py-0.5 rounded">
                              No disponible
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {selectedServicios.length > 0 && (
                <div className="p-3 bg-gray-700 border-t border-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">
                      {selectedServicios.length} {selectedServicios.length === 1 ? 'servicio' : 'servicios'} seleccionados
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mt-4 pt-3 border-t border-gray-700 gap-4">
        {/* Mostrar el precio total - Solo visible en desktop */}
        <div className="hidden lg:block order-2 md:order-1">
          {selectedServicios.length > 0 && (
            <div className="flex flex-col">
              <span className="text-sm text-gray-400">Total servicios:</span>
              <span className="text-lg font-bold text-green-400">
                ${precioTotal.toLocaleString()}
              </span>
            </div>
          )}
        </div>
        
        {/* Botones */}
        <div className="flex flex-col md:flex-row gap-3 order-1 md:order-2">
          <Button 
            variant="primary" 
            type="button"
            onClick={handleSubmitWithValidation}
            disabled={loading}
            className="w-full md:w-auto py-2.5 order-1 md:order-2"
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Guardando...
              </>
            ) : (
              editingCita ? 'Actualizar Cita' : 'Guardar Cita'
            )}
          </Button>
          <Button 
            variant="secondary" 
            type="button"
            onClick={handleCancel}
            className="w-full md:w-auto py-2.5 order-2 md:order-1"
          >
            Cancelar
          </Button>
        </div>
      </div>

      {/* Modal de creación rápida */}
      <ModalCreacionRapida 
        isOpen={showModalCreacionRapida}
        onClose={() => setShowModalCreacionRapida(false)}
        onClienteVehiculoCreado={handleClienteVehiculoCreado}
      />
      </form>
    </>
  );
};

export default AppointmentForm;
